import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { ScaffoldStep, resolveTokens, VisualData, KeyStage } from '@eduwrite/shared';
import { useWorkpadStore } from '../store/useWorkpadStore';
import { useAuthStore } from '../store/useAuthStore';
import { useStageTokens } from '../hooks/useStageTokens';
import { AudioPromptButton } from './AudioPromptButton';
import { DecompositionVisual } from './visuals/DecompositionVisual';
import { NumberLineVisual } from './visuals/NumberLineVisual';
import { ColumnLayoutVisual } from './visuals/ColumnLayoutVisual';
import { BlankCanvasVisual } from './visuals/BlankCanvasVisual';
import { PartialFillVisual } from './visuals/PartialFillVisual';

interface GuidedStepRendererProps {
    steps: ScaffoldStep[];
    templateContext?: Record<string, number | string>;
}

function renderVisual(
    visual: VisualData,
    stepId: string,
    context: Record<string, number | string>,
    onInput: (input: string) => void
) {
    if (!visual || !visual.type) {
        return <BlankCanvasVisual data={{ type: 'blank_canvas' }} context={context || {}} />;
    }
    switch (visual.type) {
        case 'decomposition':
            return <DecompositionVisual data={visual} context={context} onInput={onInput} />;
        case 'number_line':
            return <NumberLineVisual data={visual} context={context} onInput={onInput} />;
        case 'column_layout':
            return <ColumnLayoutVisual data={visual} context={context} onInput={onInput} />;
        case 'blank_canvas':
            return <BlankCanvasVisual data={visual} context={context} />;
        case 'partial_fill':
            return <PartialFillVisual data={visual} context={context} onInput={onInput} />;
        default:
            // Fallback for malformed visual data
            return <BlankCanvasVisual data={{ type: 'blank_canvas' }} context={context || {}} />;
    }
}

export const GuidedStepRenderer: React.FC<GuidedStepRendererProps> = ({ steps, templateContext = {} }) => {
    const keyStage = useAuthStore((state) => state.keyStage) as KeyStage | undefined;
    const tokens = useStageTokens(keyStage);

    const {
        currentStepIndex,
        stepProgress,
        hintVisible,
        advanceStep,
        revealHint,
        recordStepInput,
        markStepComplete,
    } = useWorkpadStore();

    const [localInput, setLocalInput] = useState('');
    const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

    const currentStep = steps[currentStepIndex];
    const currentProgress = stepProgress[currentStepIndex];

    // Inactivity timer: reveal hint after 10s of no interaction
    useEffect(() => {
        if (!currentStep?.hintText || hintVisible) return;
        inactivityTimerRef.current = setTimeout(() => {
            revealHint();
        }, 10000);
        return () => {
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
        };
    }, [currentStepIndex, currentStep?.hintText, hintVisible, revealHint]);

    // Reset inactivity timer on any interaction
    const resetInactivityTimer = () => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }
    };

    if (!currentStep) {
        return (
            <View style={styles.container}>
                <Text style={styles.completeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>All steps complete!</Text>
            </View>
        );
    }

    const resolvedInstruction = resolveTokens(currentStep.instruction, templateContext);
    const resolvedHint = currentStep.hintText ? resolveTokens(currentStep.hintText, templateContext) : undefined;

    const canAdvance =
        !currentStep.requiresStudentInput || (currentProgress?.studentInput !== undefined) || localInput.length > 0;

    const handleAdvance = () => {
        resetInactivityTimer();
        if (localInput) {
            recordStepInput(currentStep.id, localInput);
            setLocalInput('');
        }
        markStepComplete(currentStep.id);
        advanceStep();
    };

    const handleVisualInput = (input: string) => {
        resetInactivityTimer();
        setLocalInput(input);
        recordStepInput(currentStep.id, input);
    };

    const isLastStep = currentStepIndex === steps.length - 1;

    // Age-adaptive auto-play:
    // EYFS → autoPlay on every step
    // KS1 → autoPlay on first step only
    // KS2 → no autoPlay
    const shouldAutoPlay = tokens.isEYFS || (keyStage === 'KS1' && currentStepIndex === 0);

    return (
        <View style={styles.container}>
            {/* Progress dots */}
            <View style={styles.progressRow}>
                {steps.map((step, idx) => (
                    <View
                        key={step.id || `step-dot-${idx}`}
                        style={[
                            styles.dot,
                            idx === currentStepIndex && styles.dotActive,
                            idx < currentStepIndex && styles.dotCompleted,
                        ]}
                        accessibilityLabel={`Step ${idx + 1} of ${steps.length}${idx < currentStepIndex ? ' completed' : ''}${idx === currentStepIndex ? ' current' : ''}`}
                    >
                        {idx < currentStepIndex && <Text style={styles.dotCheck} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✓</Text>}
                    </View>
                ))}
            </View>
            <Text style={styles.stepLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                Step {currentStepIndex + 1} of {steps.length}
            </Text>

            {/* Instruction */}
            <View style={styles.instructionRow}>
                <Text style={styles.instruction} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{resolvedInstruction}</Text>
                <AudioPromptButton text={resolvedInstruction} autoPlay={shouldAutoPlay} />
            </View>

            {/* Visual */}
            <View style={styles.visualContainer}>
                {renderVisual(currentStep.visual, currentStep.id, templateContext, handleVisualInput)}
            </View>

            {/* Hint */}
            {hintVisible && resolvedHint && (
                <View style={styles.hintBox}>
                    <Text style={styles.hintText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{resolvedHint}</Text>
                </View>
            )}

            {/* Controls */}
            <View style={styles.controls}>
                <TouchableOpacity
                    style={styles.hintButton}
                    onPress={revealHint}
                    accessibilityLabel="Show hint"
                    accessibilityRole="button"
                    accessibilityHint="Double tap to reveal a helpful hint"
                >
                    <Text style={styles.hintButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>💡 Hint</Text>
                </TouchableOpacity>

                <TouchableOpacity
                    style={[styles.nextButton, !canAdvance && styles.nextButtonDisabled]}
                    onPress={handleAdvance}
                    disabled={!canAdvance}
                    accessibilityLabel={isLastStep ? "Finish scaffold" : "Next step"}
                    accessibilityRole="button"
                    accessibilityHint={isLastStep ? "Double tap to finish this activity" : "Double tap to go to the next step"}
                    accessibilityState={{ disabled: !canAdvance }}
                >
                    <Text style={styles.nextButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        {isLastStep ? "I've got it!" : 'Next Step'}
                    </Text>
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        paddingVertical: 8,
    },
    progressRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 8,
        marginBottom: 8,
    },
    dot: {
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: '#E2E8F0',
        borderWidth: 2,
        borderColor: '#CBD5E1',
        alignItems: 'center',
        justifyContent: 'center',
    },
    dotActive: {
        backgroundColor: '#3B82F6',
        borderColor: '#3B82F6',
    },
    dotCompleted: {
        backgroundColor: '#22c55e',
        borderColor: '#22c55e',
    },
    dotCheck: {
        color: '#FFFFFF',
        fontSize: 8,
        fontWeight: '700',
    },
    stepLabel: {
        textAlign: 'center',
        fontSize: 14,
        color: '#64748B',
        marginBottom: 12,
    },
    instructionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
    },
    instruction: {
        flex: 1,
        fontSize: 20,
        fontWeight: '600',
        color: '#1E293B',
        lineHeight: 28,
    },
    visualContainer: {
        flex: 1,
        minHeight: 120,
    },
    hintBox: {
        backgroundColor: '#FEF9C3',
        borderRadius: 12,
        padding: 12,
        marginVertical: 8,
        borderWidth: 1,
        borderColor: '#FDE047',
    },
    hintText: {
        fontSize: 16,
        color: '#854D0E',
        fontWeight: '500',
    },
    controls: {
        flexDirection: 'row',
        gap: 12,
        marginTop: 8,
    },
    hintButton: {
        flex: 1,
        height: 48,
        borderRadius: 12,
        backgroundColor: '#FEF9C3',
        borderWidth: 1,
        borderColor: '#FDE047',
        alignItems: 'center',
        justifyContent: 'center',
    },
    hintButtonText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#854D0E',
    },
    nextButton: {
        flex: 2,
        height: 48,
        borderRadius: 12,
        backgroundColor: '#22c55e',
        alignItems: 'center',
        justifyContent: 'center',
    },
    nextButtonDisabled: {
        backgroundColor: '#E2E8F0',
    },
    nextButtonText: {
        fontSize: 16,
        fontWeight: '700',
        color: '#FFFFFF',
    },
    completeText: {
        fontSize: 20,
        fontWeight: '700',
        color: '#22c55e',
        textAlign: 'center',
        marginTop: 24,
    },
});
